// ViewAgentForm.jsx
import React, { useState, useEffect, useReducer } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../SharedInputs.jsx";
import { S } from "../styles.js";
import { initialState, reducer } from "./state.js";
import { validateSection } from "./util.js";

// Import existing agent sections
import { SectionCompanyDetails } from "./Sections/SectionCompanyDetails.jsx";
import { SectionCompanyOverview } from "./Sections/SectionCompanyOverview.jsx";
import { SectionExperience } from "./Sections/SectionExperience.jsx";
import { SectionBenefits } from "./Sections/SectionBenefits.jsx";
import { SectionReferees } from "./Sections/SectionReferees.jsx";
import { SectionDeclaration } from "./Sections/SectionDeclaration.jsx";
import { SectionAction } from "./Sections/SectionAction.jsx";

export default function ViewAgentForm({ initialData, onCancel }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isNew, setIsNew] = useState(false);

  const [state, dispatch] = useReducer(
    reducer,
    initialData || initialState
  );

  const sections = [
    "companyDetails",
    "companyOverview",
    "experience",
    "benefits",
    "referees",
    "declaration",
    "actions"
  ];

  const sectionLabels = {
    companyDetails: "Company Details",
    companyOverview: "Company Overview",
    experience: "Experience",
    benefits: "Benefits",
    referees: "Referees",
    declaration: "Declaration",
    actions: "Actions"
  };

  // Auto-mark sections as complete if valid
  useEffect(() => {
    const newCompleted = [];
    sections.forEach((key, i) => {
      const valid = validateSection(key, state).valid;
      if (valid) newCompleted.push(i);
    });
    setCompletedSteps(newCompleted);
    setIsNew(state.status === 'new');
  }, [state]);

  const renderSection = () => {
    const key = sections[currentStep];
    const data = state[key] || {};
    const SectionMap = {
      companyDetails: SectionCompanyDetails,
      companyOverview: SectionCompanyOverview,
      experience: SectionExperience,
      benefits: SectionBenefits,
      referees: SectionReferees,
      declaration: SectionDeclaration,
      actions: SectionAction
    };
    const SectionComponent = SectionMap[key];
    if (!SectionComponent) return <div>Section not found</div>;

    return (
      <SectionComponent
        state={state}
        dispatch={dispatch}
        readOnly={true}
        errors={[]} // no validation messages in view mode
      />
    );
  };

  return (
    <div style={S.enrolmentPage}>
      <motion.div
        style={{ ...S.card, display: "flex", flexDirection: "column" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Header */}
        <div
          className="actions"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: "1rem",
            borderBottom: "1px solid #ddd",
            paddingBottom: "0.5rem",
          }}
        >
          <div>
            {isNew ? <h1 className="page-title">New Agent Application</h1> : <h1 className="page-title">View Agent Application</h1>}
            <p className="page-subtitle">
              Review submitted agent application details.
            </p>
          </div>

          <Button variant="secondary" onClick={onCancel}>
            Back
          </Button>
        </div>

        {/* Content Area */}
        <div style={{ display: "flex", flex: 1 }}>
          {/* Left Panel Tabs */}
          <div
            style={{
              width: 220,
              borderRight: "2px solid #ccc",
              display: "flex",
              flexDirection: "column",
              paddingTop: 16,
              backgroundColor: "#f9f9f9",
            }}
          >
            {sections.filter(k => k != "actions").map((key, index) => (
              <div
                key={key}
                onClick={() => setCurrentStep(index)}
                style={{
                  padding: "12px 16px",
                  cursor: "pointer",
                  fontWeight: currentStep === index ? "bold" : "normal",
                  color: currentStep === index ? "#007bff" : "#333",
                  backgroundColor:
                    currentStep === index ? "#e6f0ff" : "transparent",
                  borderLeft:
                    currentStep === index
                      ? "4px solid #007bff"
                      : "4px solid transparent",
                  position: "relative",
                  transition: "all 0.2s ease-in-out",
                  marginBottom: 4,
                }}
              >
                {sectionLabels[key]}
                {completedSteps.includes(index) && (
                  <span
                    style={{
                      position: "absolute",
                      right: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#28a745",
                      fontSize: 14,
                    }}
                  >
                    ✔
                  </span>
                )}
              </div>
            ))}
            <div
                key={"actions"}
                onClick={() => setCurrentStep(6)}
                style={{
                padding: "12px 16px",
                cursor: "pointer",
                fontWeight: (currentStep === 6) ? "bold" : "normal",
                color: (currentStep === 6) ? "#007bff" : "#007bff",
                backgroundColor: (currentStep === 6) ? "#e6f0ff" : "transparent",
                borderLeft:
                    (currentStep === 6) ? "4px solid #007bff" : "4px solid transparent",
                borderTop:
                    (currentStep === 6) ? "4px solid transparent" : "1px solid #007bff",
                position: "relative",
                transition: "all 0.2s ease-in-out",
                marginBottom: 4,
                }}
            >
                    {sectionLabels["actions"] || "Actions"}
            </div>
          </div>

          {/* Right Panel */}
          <div style={{ flex: 1, padding: 16 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
              >
                {renderSection()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
